// Server & Sendeabwicklung: Zustand der Kanäle, Live-Zugänge, Icecast/SHOUTcast einstellen.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { EngineBadge, Icon, IngestCard, SrtCard, StatusBadge, StatusDot, engineHint, errorText, randomPassword } from "../components/streaming-ui";
import { Callout, Card, EmptyState, EqBars, Page, PageHeader, Skeleton, StatTile } from "../components/ui";
import { ChannelAlerts, NotificationSettings, PlayoutSettingsForm, ProgramLog, useOpenAlerts } from "../components/playout-ui";
import { MessageInbox, PublicSettingsPanel, RequestInbox } from "../components/listener-ui";
import { UebergabeCard } from "../components/uebergabe-card";
import type { ChannelAlert, ChannelStream, EngineOverviewChannel, ServerKind, StreamingServer } from "../types";

export function ServersPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [channels, setChannels] = useState<EngineOverviewChannel[]>([]);
  const [servers, setServers] = useState<StreamingServer[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const alerts = useOpenAlerts(10_000);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [overview, srv] = await Promise.all([api.getEngineOverview(), api.listServers()]);
      setChannels(overview.channels);
      setServers(srv);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const apply = async (): Promise<void> => {
    setApplying(true);
    try {
      const res = await api.applyStreaming();
      setNotice(res.errors.length ? `Übernommen, aber mit Fehlern: ${res.errors.map((e) => `${e.scope}: ${e.message}`).join("; ")}` : "Alles übernommen.");
      await refresh();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setApplying(false);
    }
  };

  const managed = servers.filter((s) => s.managed);
  const external = servers.filter((s) => !s.managed);

  // Kennzahlen für die Leiste oben – alles aus Daten, die ohnehin geladen sind.
  const aktive = channels.filter((c) => c.status === "active");
  const sendend = aktive.filter((c) => c.engine?.state === "running").length;
  const hoerer = channels.reduce((sum, c) => sum + c.streams.reduce((n, st) => n + (st.listeners ?? 0), 0), 0);
  const erreichbar = servers.filter((s) => s.status?.reachable).length;

  return (
    <Page>
      <PageHeader
        eyebrow="Sendebetrieb"
        title="Server & Sendeabwicklung"
        description={
          <>
            Jeder aktive Kanal läuft in der Sende-Engine und schickt seinen Stream an Icecast, SHOUTcast und HLS.
            Änderungen übernimmt die Suite im laufenden Betrieb: Ein neuer oder geänderter Ausgang verbindet sich neu,
            alle anderen Streams und ein Moderator am Live-Eingang merken davon nichts.
          </>
        }
        actions={
          isAdmin && (
            <button onClick={apply} disabled={applying} className="btn btn-primary" title="Nicht auf den nächsten automatischen Abgleich warten">
              <Icon name="refresh" /> {applying ? "Übernehme …" : "Jetzt übernehmen"}
            </button>
          )
        }
      />

      <div className="space-y-8">
        {error && <Callout tone="danger">{error}</Callout>}
        {notice && (
          <div onClick={() => setNotice(null)} className="cursor-pointer">
            <Callout tone={notice.startsWith("Alles") ? "success" : "warning"}>{notice}</Callout>
          </div>
        )}

        {loaded && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="Kanäle auf Sendung" value={sendend} unit={`von ${aktive.length} aktiv`} tone={aktive.length > 0 && sendend === aktive.length ? "success" : aktive.length > 0 ? "warning" : "neutral"} icon={<Icon name="music" />} />
            <StatTile label="Hörer gerade" value={hoerer} tone="brand" icon={<Icon name="users" />} />
            <StatTile label="Server erreichbar" value={erreichbar} unit={`von ${servers.length}`} tone={erreichbar === servers.length ? "success" : "danger"} icon={<Icon name="server" />} />
            <StatTile label="Offene Störungen" value={alerts.open.length} tone={alerts.open.length > 0 ? "danger" : "success"} icon={<Icon name="alert" />} hint={alerts.recent.length > 0 ? `${alerts.recent.length} behoben in 7 Tagen` : undefined} />
          </div>
        )}

        <section className="space-y-3">
          <h2 className="section-label">Kanäle auf Sendung</h2>
          {!loaded ? (
            <Skeleton className="h-48" />
          ) : channels.length === 0 ? (
            <EmptyState
              icon={<Icon name="music" />}
              title="Noch keine Kanäle"
              description="Ein Kanal ist das, was gesendet wird – mit eigenen Streams und Live-Zugang."
              action={<Link to="/channels" className="btn btn-primary">Kanal anlegen</Link>}
            />
          ) : (
            channels.map((c) => (
              <ChannelOnAir key={c.id} channel={c} canAdmin={isAdmin} onChanged={refresh} alerts={alerts.open.filter((a) => a.channel_id === c.id)} />
            ))
          )}
        </section>

        {isAdmin && (
          <section className="space-y-3">
            <h2 className="section-label">Benachrichtigungen bei Störungen</h2>
            <Card>
              <NotificationSettings />
              {alerts.recent.length > 0 && (
                <details className="mt-4 border-t border-default pt-3 text-sm">
                  <summary className="cursor-pointer font-medium text-secondary">Behobene Störungen der letzten 7 Tage ({alerts.recent.length})</summary>
                  <ul className="mt-2 divide-y divide-[var(--border)]">
                    {alerts.recent.map((a) => (
                      <li key={a.id} className="flex flex-wrap gap-x-2 py-1.5 text-xs text-muted">
                        <span className="tabular-nums">{new Date(a.started_at).toLocaleString("de-DE")} – {new Date(a.resolved_at!).toLocaleTimeString("de-DE")}</span>
                        <span>· <span className="font-medium text-secondary">{a.channel_name}:</span> {a.message}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Card>
          </section>
        )}

        <section className="space-y-3">
          <h2 className="section-label">Von der Suite betriebene Server</h2>
          {user?.role !== "super_admin" && (
            <p className="text-sm text-muted">
              Diese Server gehören der Installation. Einstellen darf sie nur, wer die Installation betreibt (super_admin).
            </p>
          )}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {managed.map((s) => (
              <ServerCard key={s.id} server={s} onSaved={refresh} />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="section-label">Externe Server</h2>
              <p className="mt-1 max-w-3xl text-sm text-muted">
                Ein Icecast- oder SHOUTcast-Server, den ihr woanders gemietet habt. Die Engine sendet dorthin wie zu den eigenen;
                die Konfiguration des Servers selbst bleibt beim Anbieter. Nutzen lässt er sich danach in den Ausgängen eines Kanals.
              </p>
            </div>
          </div>
          {external.length > 0 && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {external.map((s) => (
                <ServerCard key={s.id} server={s} onSaved={refresh} />
              ))}
            </div>
          )}
          {isAdmin && <AddExternalServer onCreated={refresh} />}
        </section>
      </div>
    </Page>
  );
}

/** Umschalter unter der Kanalkarte: ein Reiter offen oder keiner. */
function PanelTab({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={`tab whitespace-nowrap text-xs sm:text-sm ${on ? "tab-active" : ""}`} aria-pressed={on}>
      {children}
    </button>
  );
}

export function ChannelOnAir({ channel, canAdmin, onChanged, alerts = [] }: {
  channel: EngineOverviewChannel; canAdmin: boolean; onChanged: () => Promise<void>; alerts?: ChannelAlert[];
}): JSX.Element {
  const { user } = useAuth();
  const canModerate = canAdmin || user?.role === "moderator";
  const active = channel.status === "active";
  const [log, setLog] = useState<string | null>(null);
  const [panel, setPanel] = useState<"none" | "program" | "rules" | "listeners">("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const engine = channel.engine;
  const np = channel.now_playing;
  const listeners = channel.streams.reduce((sum, s) => sum + (s.listeners ?? 0), 0);
  const running = active && engine?.state === "running";

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`card overflow-hidden ${running && np?.on_air === "live" ? "ring-1 ring-red-500/30" : ""}`}>
      {/* Kopf: Name, Zustand, Hörer, Hauptaktionen */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-default px-5 py-3.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <Link to={`/channels/${channel.slug}`} className="truncate text-[0.95rem] font-semibold text-primary hover:text-brand-primary">
            {channel.name}
          </Link>
          <EngineBadge state={engine?.state ?? null} active={active} />
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg px-2 text-sm text-secondary" title="Hörer über alle Streams">
            <Icon name="users" className="h-4 w-4 text-muted" />
            <span className="font-semibold tabular-nums text-primary">{listeners}</span>
          </span>
          {active && engine?.state === "running" && (
            <button disabled={busy} onClick={() => run(() => api.skipTrack(channel.slug))} className="btn btn-secondary btn-sm">
              <Icon name="skip" className="h-3.5 w-3.5" /> Nächster Titel
            </button>
          )}
          {canAdmin && active && (
            <button
              onClick={async () => {
                if (log !== null) return setLog(null);
                try {
                  const res = await api.getChannelEngineLog(channel.slug);
                  setLog(res.log || "(noch keine Ausgabe)");
                } catch (err) {
                  setError(errorText(err));
                }
              }}
              className="btn btn-ghost btn-sm"
            >
              <Icon name="log" className="h-3.5 w-3.5" /> {log !== null ? "Log schließen" : "Log"}
            </button>
          )}
        </div>
      </div>

      {/* Jetzt läuft */}
      {active && np && (
        <div className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 text-sm ${np.on_air === "live" ? "bg-red-500/[0.05]" : "bg-brand-primary/[0.04]"}`}>
          <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider ${np.on_air === "live" ? "bg-red-600 text-white" : "bg-card text-secondary ring-1 ring-[var(--border)]"}`}>
            {np.on_air === "live" && <span className="h-1.5 w-1.5 animate-live-pulse rounded-full bg-white" aria-hidden="true" />}
            {np.on_air === "live" ? (np.live_input === "srt" ? "LIVE · SRT" : "LIVE") : "Automatik"}
          </span>
          {np.silent && <span className="badge badge-warning font-semibold">STILLE</span>}
          {!np.silent && np.live_connected && np.on_air !== "live" && (
            <span className="badge badge-warning" title="Der Moderator ist verbunden, sendet aber seit einer Weile keinen Ton.">Live verbunden, aber stumm</span>
          )}
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {running && !np.silent && <EqBars className="text-brand-primary" />}
            <span className="truncate font-medium text-primary">{[np.artist, np.title].filter(Boolean).join(" – ") || "ohne Titelangabe"}</span>
          </span>
        </div>
      )}
      {!active && <div className="px-5 py-3 text-sm text-muted">Nur aktive Kanäle senden. Status in den Kanal-Einstellungen ändern.</div>}

      <div className="space-y-3 px-5 pb-5">
        {error && <div className="pt-3"><Callout tone="danger">{error}</Callout></div>}
        {active && <ChannelAlerts alerts={alerts} />}

        {active && engine && engine.state !== "running" && (
          <div className="pt-3">
            <Callout tone="warning">
              {engineHint(engine.state)}
              {engine.error && <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">{engine.error}</pre>}
            </Callout>
          </div>
        )}
        {active && engine?.state === "running" && engine.restarts > 0 && engine.error && (
          <div className="pt-3 text-xs text-muted" title={engine.error}>
            Nach {engine.restarts} Neustart(s) wieder auf Sendung.
          </div>
        )}

        {active && (
          <div className="grid grid-cols-1 gap-5 pt-2 lg:grid-cols-2">
            <div className="min-w-0">
              <div className="section-label mb-2.5">Hörer-Streams</div>
              <StreamList channel={channel} />
            </div>
            <div className="min-w-0">
              <div className="section-label mb-2.5">Live senden</div>
              {channel.ingest ? (
                <IngestCard
                  ingest={channel.ingest}
                  onRenew={
                    canAdmin
                      ? () => {
                          if (window.confirm("Neues Live-Passwort erzeugen? Wer gerade live ist, wird getrennt und muss das neue Passwort eintragen.")) {
                            void run(() => api.renewLivePassword(channel.slug));
                          }
                        }
                      : undefined
                  }
                />
              ) : (
                <div className="text-sm text-muted">Zugangsdaten sehen nur Moderatoren und Admins.</div>
              )}
              <UebergabeCard slug={channel.slug} canBroadcast={Boolean(channel.ingest)} />
              {channel.srt && (
                <div className="mt-5 border-t border-default pt-4">
                  <div className="section-label mb-2.5">SRT-Eingang (OBS, Handy-Apps)</div>
                  <SrtCard
                    srt={channel.srt}
                    connected={typeof np?.srt_connected === "boolean" ? np.srt_connected : null}
                    onToggle={canAdmin ? (enabled) => void run(() => api.setSrtEnabled(channel.slug, enabled)) : undefined}
                    onRenew={
                      canAdmin
                        ? () => {
                            if (window.confirm("Neue SRT-Passphrase erzeugen? Der Kanal startet dafür kurz neu (Hörer hören wenige Sekunden nichts), und OBS bzw. die App braucht die neue Adresse.")) {
                              void run(() => api.renewSrtPassphrase(channel.slug));
                            }
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {active && (
          <div className="border-t border-default pt-4">
            <div className="tabbar max-w-full overflow-x-auto" role="tablist">
              <PanelTab on={panel === "program"} onClick={() => setPanel(panel === "program" ? "none" : "program")}>Was lief · Als Nächstes</PanelTab>
              <PanelTab on={panel === "listeners"} onClick={() => setPanel(panel === "listeners" ? "none" : "listeners")}>Hörerseite & Wünsche</PanelTab>
              {canAdmin && (
                <PanelTab on={panel === "rules"} onClick={() => setPanel(panel === "rules" ? "none" : "rules")}>Lautstärke, Stille-Wächter, Wiederholungen</PanelTab>
              )}
            </div>
          </div>
        )}
        {panel === "program" && <div className="well !p-4"><ProgramLog slug={channel.slug} /></div>}
        {panel === "rules" && <div className="well !p-4"><PlayoutSettingsForm slug={channel.slug} /></div>}
        {panel === "listeners" && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div><div className="section-label mb-2">Wünsche</div><RequestInbox slug={channel.slug} /></div>
            <div><div className="section-label mb-2">Nachrichten ins Studio</div><MessageInbox slug={channel.slug} canModerate={canModerate} /></div>
            {canAdmin && <div><div className="section-label mb-2">Hörerseite</div><PublicSettingsPanel slug={channel.slug} /></div>}
          </div>
        )}
        {active && panel !== "listeners" && <RequestInbox slug={channel.slug} compact />}
        {active && panel !== "listeners" && <MessageInbox slug={channel.slug} canModerate={canModerate} compact />}

        {log !== null && (
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-default bg-input p-3 font-mono text-xs text-primary">{log}</pre>
        )}
      </div>
    </section>
  );
}

const OUTPUT_STATE_TEXT: Record<string, string> = {
  connecting: "verbindet sich …",
  connected: "verbunden",
  disconnected: "getrennt, verbindet sich neu",
  error: "Fehler",
};

function streamTone(s: ChannelStream): { tone: "ok" | "warn" | "error" | "idle"; title: string } {
  if (!s.enabled) return { tone: "idle", title: "deaktiviert" };
  const eo = s.engine_output;
  if (eo?.state === "error") return { tone: "error", title: `Engine: ${eo.message || "Fehler"}` };
  if (s.kind === "hls") return eo ? { tone: "ok", title: "HLS wird von der Engine geschrieben" } : { tone: "warn", title: "Engine meldet diesen Ausgang noch nicht" };
  if (s.server && !s.server.reachable) return { tone: "error", title: `${s.server.name} nicht erreichbar` };
  if (s.online) return { tone: "ok", title: "Server empfängt den Stream" };
  if (eo) return { tone: "warn", title: `Engine: ${OUTPUT_STATE_TEXT[eo.state] ?? eo.state}${eo.message ? ` – ${eo.message}` : ""}` };
  return { tone: "warn", title: "Server empfängt noch nichts" };
}

function StreamList({ channel }: { channel: EngineOverviewChannel }): JSX.Element {
  if (channel.streams.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-default px-4 py-5 text-sm text-muted">
        Keine Ausgänge. <Link to={`/channels/${channel.slug}`} className="font-medium text-brand-primary hover:underline">Ausgang hinzufügen</Link>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-default">
      {channel.streams.map((s) => {
        const { tone, title } = streamTone(s);
        return (
          <li key={s.id} className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-sm">
            <span title={title}>
              <StatusDot tone={tone} />
            </span>
            <span className="w-24 flex-shrink-0 font-medium text-primary">
              {s.kind === "hls" ? "HLS" : s.kind === "icecast" ? "Icecast" : `SHOUTcast #${s.sid}`}
            </span>
            <span className="badge badge-neutral flex-shrink-0 font-mono tabular-nums">
              {s.codec.toUpperCase().replace("_PLUS", "+")} {s.bitrate_kbps}
            </span>
            {s.url ? (
              <a href={s.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 basis-40 truncate font-mono text-xs text-muted hover:text-brand-primary hover:underline">
                {s.url}
              </a>
            ) : (
              <span className="min-w-0 flex-1 text-xs text-muted">ohne Server</span>
            )}
            {s.listeners !== null && (
              <span className="inline-flex items-center gap-1 text-xs tabular-nums text-secondary" title="Hörer auf diesem Stream">
                <Icon name="users" className="h-3.5 w-3.5 text-muted" /> {s.listeners}
              </span>
            )}
            {tone === "error" && <span className="w-full pl-5 text-xs text-red-600 dark:text-red-400">{title}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function ServerCard({ server, onSaved }: { server: StreamingServer; onSaved: () => Promise<void> }): JSX.Element {
  const status = server.status;
  const [editing, setEditing] = useState(false);
  const [config, setConfig] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const deleteServer = async (): Promise<void> => {
    if (!window.confirm(`Server „${server.name}" löschen?`)) return;
    try {
      await api.deleteServer(server.id);
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const auslastung = server.max_listeners > 0 ? Math.min(100, Math.round(((status?.listeners ?? 0) / server.max_listeners) * 100)) : 0;

  return (
    <section className="card flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 p-5 pb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-input text-secondary ring-1 ring-[var(--border)]">
              <Icon name="server" />
            </span>
            <span className="text-[0.95rem] font-semibold text-primary">{server.name}</span>
            <span className="badge badge-neutral">{server.kind === "icecast" ? "Icecast" : "SHOUTcast"}</span>
            <StatusBadge tone={status?.reachable ? "ok" : "error"}>{status?.reachable ? "erreichbar" : "nicht erreichbar"}</StatusBadge>
          </div>
          <div className="mt-1.5 text-xs text-muted">
            {status?.reachable ? status.version ?? "erreichbar" : `nicht erreichbar${status?.error ? `: ${status.error}` : ""}`}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[1.6rem] font-semibold leading-none tracking-tight tabular-nums text-primary">{status?.listeners ?? 0}</div>
          <div className="mt-1 text-xs text-muted">von {server.max_listeners} Hörern</div>
          <div className="ml-auto mt-1.5 h-1 w-24 overflow-hidden rounded-full bg-input ring-1 ring-[var(--border)]" aria-hidden="true">
            <div className="h-full rounded-full bg-brand-primary" style={{ width: `${auslastung}%` }} />
          </div>
        </div>
      </div>

      <div className="mx-5 space-y-1 rounded-lg bg-input px-3 py-2 text-xs text-secondary ring-1 ring-[var(--border)]">
        <div className="flex min-w-0 gap-2"><span className="w-24 flex-shrink-0 text-muted">Hörer-Adresse</span><span className="min-w-0 truncate font-mono">{server.public_url}</span></div>
        {!server.managed && (
          <div className="flex min-w-0 gap-2"><span className="w-24 flex-shrink-0 text-muted">Engine sendet an</span><span className="min-w-0 truncate font-mono">{server.host}:{server.port}</span></div>
        )}
      </div>

      {status?.reachable && (
        <ul className="mx-5 mt-3 divide-y divide-[var(--border)]">
          {status.streams.length === 0 ? (
            <li className="py-1.5 text-xs text-muted">Keine Streams verbunden.</li>
          ) : (
            status.streams.map((s) => (
              <li key={s.mount} className="flex min-w-0 items-center gap-2 py-1.5 text-xs">
                <StatusDot tone={s.online ? "ok" : "idle"} />
                <span className="font-mono text-primary">{s.mount}</span>
                <span className="tabular-nums text-muted">{s.bitrate_kbps ? `${s.bitrate_kbps} kbps` : ""}</span>
                <span className="min-w-0 flex-1 truncate text-muted">{s.title}</span>
                <span className="tabular-nums text-secondary">{s.listeners}</span>
              </li>
            ))
          )}
        </ul>
      )}

      {error && <div className="mx-5 mt-3"><Callout tone="danger">{error}</Callout></div>}

      <div className="flex-1" />
      {server.editable && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-default px-5 py-3">
          <button onClick={() => setEditing((e) => !e)} className="btn btn-secondary btn-sm">
            {editing ? "Schließen" : "Einstellungen"}
          </button>
          {server.managed && (
            <button
              onClick={async () => {
                if (config !== null) return setConfig(null);
                try {
                  setConfig((await api.getServerConfig(server.id)).content);
                } catch (err) {
                  setError(errorText(err));
                }
              }}
              className="btn btn-ghost btn-sm"
            >
              {config !== null ? "Konfiguration schließen" : "Erzeugte Konfiguration"}
            </button>
          )}
          {!server.managed && (
            <button onClick={deleteServer} className="btn btn-ghost btn-sm ml-auto text-red-600 dark:text-red-400">
              <Icon name="trash" className="h-3.5 w-3.5" /> Löschen
            </button>
          )}
        </div>
      )}
      {!server.editable && <div className="h-5" />}

      {editing && <div className="px-5 pb-5"><ServerForm server={server} onSaved={async () => { setEditing(false); await onSaved(); }} /></div>}
      {config !== null && (
        <pre className="mx-5 mb-5 max-h-80 overflow-auto rounded-lg border border-default bg-input p-3 font-mono text-xs text-primary">{config}</pre>
      )}
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="field-label !mb-0">{label}</span>
      {children}
      {hint && <span className="field-hint block">{hint}</span>}
    </label>
  );
}

const inputClass = "input mt-1.5";

function PasswordInput({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="mt-1.5 flex gap-1.5">
      <input className="input font-mono" value={value} onChange={(e) => onChange(e.target.value)} minLength={6} required />
      <button type="button" onClick={() => onChange(randomPassword())} className="btn btn-secondary" title="Zufälliges Passwort">
        Neu
      </button>
    </div>
  );
}

function ServerForm({ server, onSaved }: { server: StreamingServer; onSaved: () => Promise<void> }): JSX.Element {
  const [form, setForm] = useState({
    name: server.name,
    host: server.host,
    port: server.port,
    public_url: server.public_url,
    source_password: server.source_password ?? "",
    admin_user: server.admin_user,
    admin_password: server.admin_password ?? "",
    max_listeners: server.max_listeners,
    location: server.settings.location ?? "",
    admin_email: server.settings.admin_email ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (form.source_password !== server.source_password &&
        !window.confirm("Das Quell-Passwort ändert sich. Die Engine verbindet sich danach neu; Hörer hören dabei einige Sekunden nichts. Fortfahren?")) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateServer(server.id, {
        name: form.name,
        ...(server.managed ? {} : { host: form.host, port: Number(form.port) }),
        public_url: form.public_url,
        source_password: form.source_password,
        admin_user: form.admin_user,
        admin_password: form.admin_password,
        max_listeners: Number(form.max_listeners),
        settings: server.kind === "icecast" ? { location: form.location || undefined, admin_email: form.admin_email || undefined } : {},
      });
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 border-t border-default pt-4 sm:grid-cols-2">
      <Field label="Name">
        <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </Field>
      <Field label="Hörer maximal">
        <input type="number" min={1} className={inputClass} value={form.max_listeners} onChange={(e) => setForm({ ...form, max_listeners: Number(e.target.value) })} />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Öffentliche Adresse" hint="So erreichen Hörer den Server, z. B. https://stream.meinradio.de. Daraus entstehen die Stream-Links.">
          <input type="url" className={inputClass} value={form.public_url} onChange={(e) => setForm({ ...form, public_url: e.target.value })} required />
        </Field>
      </div>
      {!server.managed && (
        <>
          <Field label="Host" hint="Ohne http:// und Port">
            <input className={inputClass} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} required />
          </Field>
          <Field label="Port">
            <input type="number" min={1} max={65535} className={inputClass} value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
          </Field>
        </>
      )}
      <Field label="Quell-Passwort" hint="Damit sendet die Engine an den Server.">
        <PasswordInput value={form.source_password} onChange={(v) => setForm({ ...form, source_password: v })} />
      </Field>
      <Field label="Admin-Passwort">
        <PasswordInput value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} />
      </Field>
      {server.kind === "icecast" && (
        <>
          <Field label="Admin-Benutzer">
            <input className={inputClass} value={form.admin_user} onChange={(e) => setForm({ ...form, admin_user: e.target.value })} />
          </Field>
          <Field label="Kontakt-E-Mail" hint="Erscheint auf der Statusseite von Icecast.">
            <input type="email" className={inputClass} value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
          </Field>
          <Field label="Standort">
            <input className={inputClass} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </Field>
        </>
      )}
      {error && <div className="sm:col-span-2"><Callout tone="danger">{error}</Callout></div>}
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} className="btn btn-primary">
          {busy ? "Speichere …" : "Speichern"}
        </button>
      </div>
    </form>
  );
}

function AddExternalServer({ onCreated }: { onCreated: () => Promise<void> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const empty = { kind: "icecast" as ServerKind, name: "", host: "", port: 8000, public_url: "", source_password: "", admin_user: "admin", admin_password: "", max_listeners: 100 };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn btn-secondary">
        <Icon name="plus" /> Externen Server hinzufügen
      </button>
    );
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createServer({ ...form, port: Number(form.port), max_listeners: Number(form.max_listeners) });
      setForm(empty);
      setOpen(false);
      await onCreated();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <h3 className="card-title">Externen Server hinzufügen</h3>
      </div>
      <Field label="Art">
        <select className={inputClass} value={form.kind} onChange={(e) => {
          const kind = e.target.value as ServerKind;
          setForm({ ...form, kind, port: kind === "icecast" ? 8000 : 8002 });
        }}>
          <option value="icecast">Icecast 2</option>
          <option value="shoutcast">SHOUTcast 2 (DNAS)</option>
        </select>
      </Field>
      <Field label="Name">
        <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Stream-Hoster" required />
      </Field>
      <Field label="Host" hint="Ohne http:// und Port">
        <input className={inputClass} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="stream.anbieter.de" required />
      </Field>
      <Field label="Port" hint={form.kind === "shoutcast" ? "Der „portbase“ des DNAS" : undefined}>
        <input type="number" min={1} max={65535} className={inputClass} value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Öffentliche Adresse" hint="So erreichen Hörer den Server, z. B. https://stream.anbieter.de:8000">
          <input type="url" className={inputClass} value={form.public_url} onChange={(e) => setForm({ ...form, public_url: e.target.value })} required />
        </Field>
      </div>
      <Field label="Quell-Passwort">
        <PasswordInput value={form.source_password} onChange={(v) => setForm({ ...form, source_password: v })} />
      </Field>
      <Field label="Admin-Passwort">
        <PasswordInput value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} />
      </Field>
      {error && <div className="sm:col-span-2"><Callout tone="danger">{error}</Callout></div>}
      <div className="flex gap-2 sm:col-span-2">
        <button type="submit" disabled={busy} className="btn btn-primary">
          {busy ? "Lege an …" : "Anlegen"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost">
          Abbrechen
        </button>
      </div>
    </form>
  );
}
